/**
 * Vincent #302 (2026-06-12) — Importer un fichier textuel (.txt, .md, .docx, .pdf).
 * Eric (2026-06-14) — Suggestion implémentée : **drag-and-drop MULTIPLE** +
 * upload séquentiel + lancement automatique batch Q&R sur tous les documents
 * importés (un dossier de 10 procédures → 1 KB complète).
 */
import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  FileText, CloudArrowUp, X, SpinnerGap, CheckCircle, ArrowRight, Warning, Sparkle,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { API_BASE as API } from "../lib/apiBase";

// Tâche 1435 — La liste vit désormais dans `lib/formatsImport.js`, partagée
// avec la zone d'import groupé et verrouillée sur celle du serveur.
const ACCEPTED = ACCEPT_DOC;
const ACCEPTED_EXT_LIST = EXTENSIONS_DOC;

// Vincent B629 (Eric 2026-07-30 SPEC STRICK PROD) — Thèmes FR-only depuis
// la source unique lib/trainingThemes.js. Fin de la duplication qui laissait
// « Procédures » anglais (avec accent) ou « Sales » dans l'UI FR.
import { TRAINING_THEMES, themeLabelFor } from "../lib/trainingThemes";
import { ACCEPT_DOC, EXTENSIONS_DOC, LISTE_DOC, connaissanceAutorisee } from "../lib/formatsImport";
import { signalerRefusNavigateur } from "../lib/refusImport";
import ImportRefusalsPanel from "../components/text/ImportRefusalsPanel";
import RelanceGroupeePanel from "../components/text/RelanceGroupeePanel";
import NettoyageEssaisPanel from "../components/text/NettoyageEssaisPanel";

const STATUS_STYLES = {
  pending: "text-zinc-500 bg-zinc-100",
  uploading: "text-blue-700 bg-blue-100",
  done: "text-teal-700 bg-teal-100",
  error: "text-rose-700 bg-rose-100",
  qa_running: "text-violet-700 bg-violet-100",
  qa_done: "text-teal-700 bg-teal-100",
};

export default function UploadTextPage() {
  const { lang } = useLang();
  const L = (fr, en) => (lang === "fr" ? fr : en);
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  // Eric (2026-06-14) — Nouvelle architecture multi-fichiers : on garde une
  // queue d'items {file, title, status, doc_id, error}.
  const [items, setItems] = useState([]);
  const [theme, setTheme] = useState("general");
  const [category, setCategory] = useState("");
  // Vincent B628 (Eric 2026-07-30 SPEC STRICK PROD) — La case à cocher
  // « Lancer la génération Q&R automatiquement » a été retirée : la
  // génération Q&R démarre TOUJOURS automatiquement après chaque import
  // (comportement par défaut, plus de toggle exposé à l'utilisateur).
  const autoGenerateQA = true;
  const [dragActive, setDragActive] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pathways, setPathways] = useState([]);
  const [selectedPathwayIds, setSelectedPathwayIds] = useState([]);
  // Tâche 1478 — Identifiant de l'item dont l'aperçu de classeur est ouvert.
  const [apercuOuvert, setApercuOuvert] = useState(null);

  useEffect(() => {
    const blockDefault = (e) => { e.preventDefault(); };
    window.addEventListener("dragover", blockDefault);
    window.addEventListener("drop", blockDefault);
    return () => {
      window.removeEventListener("dragover", blockDefault);
      window.removeEventListener("drop", blockDefault);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/pathways`, { withCredentials: true });
        if (!cancelled) setPathways(Array.isArray(res.data) ? res.data : (res.data?.pathways || []));
      } catch (_) { setPathways([]); }
    })();
    return () => { cancelled = true; };
  }, []);

  const addFiles = (filesList) => {
    if (!filesList || filesList.length === 0) return;
    const accepted = [];
    const rejected = [];
    const rejetesBruts = [];
    for (const f of filesList) {
      const ok = ACCEPTED_EXT_LIST.some((ext) => (f.name || "").toLowerCase().endsWith(ext));
      if (ok) {
        accepted.push({
          id: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 6)}`,
          file: f,
          title: f.name.replace(/\.[^.]+$/, ""),
          status: "pending",
          doc_id: null,
          error: null,
          preview: null, // Eric 2026-06-26 (iter128 P1) — rich preview pour .docx
        });
      } else {
        rejected.push(f.name);
        rejetesBruts.push(f);
      }
    }
    if (rejected.length > 0) {
      toast.warning(L(
        `${rejected.length} fichier(s) ignoré(s) — format non supporté : ${rejected.slice(0, 3).join(", ")}`,
        `${rejected.length} file(s) skipped — unsupported format: ${rejected.slice(0, 3).join(", ")}`
      ));
      // Tâche 1438 — Cet écran refusait aussi dans le navigateur, SANS rien
      // déclarer : le journal des refus paraissait complet alors qu'il
      // ignorait une des deux portes d'entrée.
      signalerRefusNavigateur(
        rejetesBruts, `Extension hors de la liste acceptée (${LISTE_DOC})`);
    }
    if (accepted.length > 0) {
      setItems((prev) => [...prev, ...accepted]);
      // Eric 2026-06-26 (iter128 P1) — Pré-extraction asynchrone des .docx
      // pour montrer un compteur (headings, tables, paragraphes) dans la liste.
      for (const it of accepted) {
        if (it.file.name.toLowerCase().endsWith(".docx")) {
          fetchPreview(it.id, it.file);
        }
        // Tâche 1478 — Un classeur Excel montre ses premières lignes AVANT de
        // créer le moindre document : corriger un mauvais fichier coûte alors
        // un clic, et non un lot de documents à supprimer plus la facture IA.
        if (it.file.name.toLowerCase().endsWith(".xlsx")) {
          fetchApercuXlsx(it.id, it.file);
        }
      }
    }
  };

  const fetchPreview = async (itemId, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/api/text-documents/preview`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      updateItem(itemId, { preview: r.data });
    } catch (_) {
      // non-bloquant — l'import procédera quand même
    }
  };

  // Tâche 1478 — Aperçu d'un classeur : feuilles + premières lignes. Les
  // feuilles vides sont décochées d'office (les cocher créerait un document
  // vide, puis une facture IA pour rien).
  const fetchApercuXlsx = async (itemId, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/api/text-documents/preview-xlsx`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const feuilles = r.data?.feuilles || [];
      updateItem(itemId, {
        xlsx: r.data,
        feuillesChoisies: feuilles.filter((f) => f.exploitable).map((f) => f.nom),
      });
      setApercuOuvert(itemId);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail
        : L("Aperçu du classeur impossible", "Workbook preview failed"));
    }
  };

  const basculerFeuille = (itemId, nom) => {
    setItems((prev) => prev.map((it) => {
      if (it.id !== itemId) return it;
      const choisies = it.feuillesChoisies || [];
      return {
        ...it,
        feuillesChoisies: choisies.includes(nom)
          ? choisies.filter((n) => n !== nom)
          : [...choisies, nom],
      };
    }));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current = 0;
    setDragActive(false);
    addFiles(Array.from(e.dataTransfer?.files || []));
  };

  const updateItem = (id, patch) => {
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  };

  const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));

  const renameItem = (id, newTitle) => updateItem(id, { title: newTitle });

  const triggerQaBatch = async (docId) => {
    try {
      await axios.post(`${API}/api/agent/generate-qa-batch/${docId}`, {}, { withCredentials: true });
      return true;
    } catch (err) {
      return false;
    }
  };

  const startBatch = async () => {
    if (items.length === 0 || submitting) return;
    // Tâche 1478 — Un classeur dont AUCUNE feuille n'est cochée ne doit pas
    // partir : il recevrait un refus serveur, et l'écran aurait l'air en
    // panne alors que c'est un choix vide.
    const sansFeuille = items.find(
      (it) => it.xlsx && (it.feuillesChoisies || []).length === 0
        && it.status !== "done" && it.status !== "qa_done");
    if (sansFeuille) {
      toast.error(L(
        `Aucune feuille cochée pour « ${sansFeuille.file.name} » : ouvrez l'aperçu et choisissez au moins une feuille.`,
        `No sheet selected for “${sansFeuille.file.name}”: open the preview and pick at least one sheet.`
      ));
      setApercuOuvert(sansFeuille.id);
      return;
    }
    setSubmitting(true);
    let okCount = 0, errCount = 0, qaQueued = 0;
    for (const it of items) {
      if (it.status === "done" || it.status === "qa_done" || it.status === "qa_running") {
        okCount++;
        continue;
      }
      updateItem(it.id, { status: "uploading", error: null });
      try {
        const fd = new FormData();
        fd.append("file", it.file);
        fd.append("title", it.title.trim() || it.file.name);
        fd.append("training_theme", theme);
        if (category) fd.append("category", category);
        if (selectedPathwayIds.length > 0) fd.append("pathway_ids", selectedPathwayIds.join(","));
        // Tâche 1478 — Seules les feuilles cochées dans l'aperçu sont
        // importées. Rien n'est envoyé pour les autres formats.
        if (it.xlsx && it.feuillesChoisies) {
          fd.append("feuilles", JSON.stringify(it.feuillesChoisies));
        }
        const r = await axios.post(`${API}/api/text-documents/upload`, fd, {
          withCredentials: true,
          headers: { "Content-Type": "multipart/form-data" },
        });
        const docId = r.data?.id;
        updateItem(it.id, { status: "done", doc_id: docId });
        okCount++;
        // Tâche 1435 — Une source de référence (.png) n'autorise que titre,
        // étiquettes et synthèse : lancer les Q&R recevrait un refus serveur.
        if (autoGenerateQA && docId && connaissanceAutorisee(r.data, "qa")) {
          updateItem(it.id, { status: "qa_running" });
          const ok = await triggerQaBatch(docId);
          updateItem(it.id, { status: ok ? "qa_done" : "done", error: ok ? null : L("Q&R en attente (lancer manuellement)", "Q&A pending (run manually)") });
          if (ok) qaQueued++;
        }
      } catch (err) {
        const detail = err.response?.data?.detail;
        const msg = typeof detail === "string" ? detail : (err.message || L("Erreur d'import", "Upload error"));
        updateItem(it.id, { status: "error", error: msg });
        errCount++;
      }
    }
    setSubmitting(false);
    if (errCount === 0) {
      toast.success(L(
        `${okCount} fichier(s) importé(s)${qaQueued > 0 ? ` · Q&R lancée pour ${qaQueued}` : ""}.`,
        `${okCount} file(s) imported${qaQueued > 0 ? ` · Q&A started for ${qaQueued}` : ""}.`
      ));
    } else {
      toast.warning(L(
        `${okCount} succès · ${errCount} erreur(s). Voir détails.`,
        `${okCount} ok · ${errCount} error(s). See details.`
      ));
    }
  };

  const pendingCount = items.filter((i) => i.status === "pending").length;
  const doneCount = items.filter((i) => i.status === "done" || i.status === "qa_done").length;
  const errorCount = items.filter((i) => i.status === "error").length;
  const qaRunningCount = items.filter((i) => i.status === "qa_running").length;

  return (
    <div className="max-w-5xl mx-auto p-6" data-testid="upload-text-page">
      <div className="mb-6 flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-[#002FA7]/10 flex items-center justify-center">
          <FileText size={22} weight="duotone" className="text-[#002FA7]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">{L("Importer des fichiers", "Upload files")}</h1>
          <p className="text-sm text-zinc-500">
            {L(
              `Glissez plusieurs fichiers (${LISTE_DOC}). Chacun est importé puis la génération Q&R est lancée automatiquement. Un classeur Excel donne un document par feuille.`,
              `Drop several files (${LISTE_DOC}). Each is uploaded then (optionally) Q&A generation is auto-launched. An Excel workbook yields one document per sheet.`
            )}
          </p>
        </div>
      </div>

      {/* Vincent B627 (Eric 2026-07-30 SPEC STRICK) — Passe de grid-cols-3 (col Réglages
          en 1/3) à grid-cols-5 avec dropzone 3/5 + Réglages 2/5. Résultat : la colonne
          "Réglages appliqués à tous les fichiers" occupe une largeur proche de celle
          dans "Importer une vidéo" (max-w-2xl full-width) → labels + inputs + Select ne
          sont plus tronqués.
          Vincent B628 (Eric 2026-07-30 SPEC STRICK, précision utilisateur PROD) —
          Le layout à 2 colonnes tronquait encore le nom des parcours dans les
          checkboxes. On empile désormais VERTICALEMENT : dropzone + liste de
          fichiers en premier, puis "Réglages appliqués à tous les fichiers"
          directement en dessous, pleine largeur (max-w-5xl du container).
          Le label du bloc parcours est aussi aligné sur celui de
          "Importer une vidéo" : "Ajouter aux parcours (optionnel — sélection
          multiple)". Le `truncate` sur le nom du parcours est retiré pour
          permettre le retour à la ligne / affichage complet. */}
      {/* Tâche 1451 — le journal des refus, dans la section d'import de
          fichiers texte : c'est ici qu'on est quand un import vient d'être
          refusé. */}
      <ImportRefusalsPanel kind="document" />
      {/* Tâche 1477 — Les deux relances groupées vivent à côté du journal des
          refus : c'est le même moment, « mon import n'a rien produit ». */}
      <RelanceGroupeePanel />
      {/* Tâche 1482 — Le nettoyage est juste sous la relance groupée : c'est
          elle que ces essais encombrent. */}
      <NettoyageEssaisPanel />

      <div className="space-y-6">
        {/* Dropzone + liste fichiers (pleine largeur) */}
        <div className="space-y-4">
          <div
            onDragEnter={(e) => { e.preventDefault(); dragCounterRef.current++; setDragActive(true); }}
            onDragLeave={(e) => { e.preventDefault(); dragCounterRef.current = Math.max(0, dragCounterRef.current - 1); if (dragCounterRef.current === 0) setDragActive(false); }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-colors ${
              dragActive ? "border-[#002FA7] bg-[#002FA7]/5" : "border-zinc-300 hover:border-[#002FA7]/60 bg-white"
            }`}
            data-testid="upload-text-dropzone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED}
              multiple
              className="hidden"
              onChange={(e) => { addFiles(Array.from(e.target.files || [])); if (e.target) e.target.value = ""; }}
              data-testid="upload-text-input"
            />
            <CloudArrowUp size={48} weight="duotone" className="text-[#002FA7] mx-auto mb-2" />
            <p className="text-sm font-semibold text-zinc-700">
              {L("Glissez vos fichiers ici (plusieurs à la fois)", "Drop your files here (multiple at once)")}
            </p>
            <p className="text-xs text-zinc-500">{L("ou cliquez pour parcourir", "or click to browse")}</p>
            <p className="mt-2 text-[10px] text-zinc-400">
              {L(`Formats : ${LISTE_DOC} — Max 25 Mo par fichier`, `Formats: ${LISTE_DOC} — Max 25 MB per file`)}
            </p>
          </div>

          {items.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden" data-testid="upload-text-list">
              <div className="px-4 py-2 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-zinc-600">
                  {items.length} {L("fichier(s) à importer", "file(s) to upload")}
                </span>
                <div className="flex items-center gap-3 text-[11px] text-zinc-500">
                  {doneCount > 0 && <span className="inline-flex items-center gap-1 text-teal-600 font-semibold"><CheckCircle size={12} weight="fill" />{doneCount}</span>}
                  {qaRunningCount > 0 && <span className="inline-flex items-center gap-1 text-violet-600 font-semibold"><SpinnerGap size={12} className="animate-spin" />{qaRunningCount}</span>}
                  {errorCount > 0 && <span className="inline-flex items-center gap-1 text-rose-600 font-semibold"><Warning size={12} weight="fill" />{errorCount}</span>}
                  <button
                    type="button"
                    onClick={() => setItems([])}
                    className="text-xs text-zinc-500 hover:text-rose-600 hover:underline"
                    disabled={submitting}
                    data-testid="upload-text-clear-all"
                  >
                    {L("Tout vider", "Clear all")}
                  </button>
                </div>
              </div>
              <ul className="divide-y divide-zinc-100 max-h-[420px] overflow-y-auto">
                {items.map((it) => (
                  <li
                    key={it.id}
                    className="px-4 py-2.5 flex items-center gap-3 hover:bg-zinc-50"
                    data-testid={`upload-text-item-${it.id}`}
                  >
                    <FileText size={18} weight="duotone" className="text-[#002FA7] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        value={it.title}
                        onChange={(e) => renameItem(it.id, e.target.value)}
                        disabled={submitting || it.status !== "pending"}
                        className="w-full bg-transparent border-0 text-sm font-medium text-zinc-900 outline-none focus:bg-white focus:border-b focus:border-[#002FA7] focus:ring-0"
                        placeholder={it.file.name.replace(/\.[^.]+$/, "")}
                        data-testid={`upload-text-item-title-${it.id}`}
                      />
                      <p className="text-[10px] text-zinc-400 mt-0.5">
                        {it.file.name} · {(it.file.size / 1024).toFixed(1)} Ko
                        {it.error && <span className="ml-2 text-rose-600 font-semibold">· {it.error}</span>}
                      </p>
                      {it.preview && (
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px]" data-testid={`upload-text-item-preview-${it.id}`}>
                          {it.preview.stats?.headings > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 font-semibold" title={L("Titres détectés", "Headings detected")}>
                              <Sparkle size={10} weight="fill" />
                              {it.preview.stats.headings} {L("titre(s)", "heading(s)")}
                            </span>
                          )}
                          {it.preview.stats?.tables > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 font-semibold" title={L("Tableaux détectés", "Tables detected")}>
                              📋 {it.preview.stats.tables} {L("tableau(x)", "table(s)")}
                            </span>
                          )}
                          {it.preview.stats?.paragraphs > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">
                              {it.preview.stats.paragraphs} {L("paragraphe(s)", "paragraph(s)")}
                            </span>
                          )}
                          {it.preview.stats?.words > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-600">
                              {it.preview.stats.words} {L("mots", "words")}
                            </span>
                          )}
                        </div>
                      )}
                      {/* Tâche 1478 — Résumé du classeur + accès à l'aperçu. */}
                      {it.xlsx && (
                        <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px]" data-testid={`upload-text-item-xlsx-${it.id}`}>
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 font-semibold">
                            {(it.feuillesChoisies || []).length} / {it.xlsx.feuilles?.length || 0} {L("feuille(s) cochée(s)", "sheet(s) selected")}
                          </span>
                          <button
                            type="button"
                            onClick={() => setApercuOuvert(it.id)}
                            className="text-[#002FA7] hover:underline font-semibold"
                            data-testid={`upload-text-item-apercu-btn-${it.id}`}
                          >
                            {L("Voir l'aperçu des feuilles", "View sheet preview")}
                          </button>
                        </div>
                      )}
                    </div>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${STATUS_STYLES[it.status] || "bg-zinc-100 text-zinc-500"}`}
                      data-testid={`upload-text-item-status-${it.id}`}
                    >
                      {it.status === "pending" && L("En attente", "Pending")}
                      {it.status === "uploading" && (
                        <span className="inline-flex items-center gap-1"><SpinnerGap size={10} className="animate-spin" />{L("Envoi", "Uploading")}</span>
                      )}
                      {it.status === "done" && L("Importé", "Imported")}
                      {it.status === "qa_running" && (
                        <span className="inline-flex items-center gap-1"><SpinnerGap size={10} className="animate-spin" />{L("Q&R en cours", "Q&A running")}</span>
                      )}
                      {it.status === "qa_done" && L("Q&R OK", "Q&A OK")}
                      {it.status === "error" && L("Erreur", "Error")}
                    </span>
                    {it.doc_id && (
                      <button
                        type="button"
                        onClick={() => navigate(`/dashboard/videos/${it.doc_id}`)}
                        className="text-xs text-[#002FA7] hover:underline whitespace-nowrap font-semibold"
                        title={L("Ouvrir le document", "Open document")}
                        data-testid={`upload-text-item-open-${it.id}`}
                      >
                        {L("Ouvrir", "Open")} <ArrowRight size={11} className="inline" />
                      </button>
                    )}
                    {it.status === "pending" && !submitting && (
                      <button
                        type="button"
                        onClick={() => removeItem(it.id)}
                        className="text-zinc-400 hover:text-rose-600"
                        title={L("Retirer", "Remove")}
                        data-testid={`upload-text-item-remove-${it.id}`}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Form (settings appliqués à TOUS les fichiers du batch) — pleine largeur */}
        <div className="space-y-4 bg-white border border-zinc-200 rounded-xl p-5">
          <p className="text-xs uppercase tracking-wider text-zinc-500 font-bold">
            {L("Réglages appliqués à tous les fichiers", "Settings applied to all files")}
          </p>
          <div>
            <Label>{L("Thème de formation", "Training theme")}</Label>
            <Select value={theme} onValueChange={setTheme}>
              <SelectTrigger data-testid="upload-text-theme"><SelectValue /></SelectTrigger>
              <SelectContent>
                {TRAINING_THEMES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{themeLabelFor(t, lang)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="cat">{L("Catégorie (optionnel)", "Category (optional)")}</Label>
            <Input
              id="cat"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder={L("Ex. Onboarding clients", "E.g. Client onboarding")}
              data-testid="upload-text-category"
            />
          </div>

          {pathways.length > 0 && (
            <div className="space-y-2">
              <Label>{L("Ajouter aux parcours (optionnel — sélection multiple)", "Add to pathways (optional — multi-select)")}</Label>
              <div className="border border-zinc-200 rounded-lg max-h-56 overflow-y-auto bg-white" data-testid="upload-text-pathways">
                {/* Vincent B652 (Eric 2026-08-05 SPEC STRICK PROD) — 3 types */}
                {["onboarding", "learning", "weekly"].map((ptype) => {
                  const subset = pathways
                    .filter((p) => p.pathway_type === ptype)
                    .slice()
                    .sort((a, b) => (a.name || "").localeCompare(b.name || "", "fr", { sensitivity: "base" }));
                  if (subset.length === 0) return null;
                  return (
                    <div key={ptype} className="border-b border-zinc-100 last:border-0">
                      <div className="px-3 py-1 text-[9px] uppercase text-zinc-500 font-bold tracking-wider bg-zinc-50">
                        {ptype === "onboarding"
                          ? L("Parcours d'intégration", "Onboarding pathways")
                          : ptype === "weekly"
                            ? L("Formations hebdomadaires", "Weekly trainings")
                            : L("Parcours d'apprentissage", "Learning pathways")}
                      </div>
                      {subset.map((p) => (
                        <label
                          key={p.id}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-zinc-50 cursor-pointer"
                          data-testid={`upload-text-pathway-${p.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={selectedPathwayIds.includes(p.id)}
                            onChange={() =>
                              setSelectedPathwayIds((prev) =>
                                prev.includes(p.id) ? prev.filter((x) => x !== p.id) : [...prev, p.id]
                              )
                            }
                            className="w-4 h-4 accent-[#002FA7]"
                          />
                          <span className="flex-1">{p.name}</span>
                        </label>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <Button
            onClick={startBatch}
            disabled={pendingCount === 0 || submitting}
            className="w-full inline-flex items-center justify-center gap-2 mt-2"
            data-testid="upload-text-submit-batch"
          >
            {submitting && <SpinnerGap size={16} className="animate-spin" />}
            {submitting
              ? L("Import en cours…", "Uploading…")
              : pendingCount > 0
              ? L(`Importer ${pendingCount} fichier(s)`, `Upload ${pendingCount} file(s)`)
              : L("Ajoutez des fichiers", "Add files")}
          </Button>
        </div>
      </div>

      {/* Tâche 1478 (Eric) — Aperçu d'un classeur Excel AVANT l'import :
          chaque feuille montre ses premières lignes, et une case à cocher
          décide si elle devient un document. Corriger un mauvais fichier
          coûte ici un clic, au lieu d'un lot de documents à supprimer et
          d'une facture IA déjà payée. */}
      {apercuOuvert && (() => {
        const it = items.find((x) => x.id === apercuOuvert);
        if (!it || !it.xlsx) return null;
        const choisies = it.feuillesChoisies || [];
        return (
          <div
            className="fixed inset-0 z-50 bg-zinc-900/60 flex items-center justify-center p-4"
            data-testid="xlsx-apercu-modal"
          >
            <div className="bg-white rounded-xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
              <div className="px-5 py-4 border-b border-zinc-200 flex items-start justify-between gap-4">
                <div>
                  <h3 className="font-semibold text-zinc-900" data-testid="xlsx-apercu-titre">
                    {L("Aperçu du classeur", "Workbook preview")} · {it.file.name}
                  </h3>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {L(
                      `Les ${it.xlsx.lignes_apercu} premières lignes de chaque feuille. Une feuille cochée = un document créé.`,
                      `The first ${it.xlsx.lignes_apercu} rows of each sheet. One checked sheet = one document created.`
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setApercuOuvert(null)}
                  className="text-zinc-400 hover:text-zinc-700"
                  data-testid="xlsx-apercu-fermer"
                >
                  <X size={18} />
                </button>
              </div>
              <div className="overflow-y-auto flex-1 p-5 space-y-5">
                {(it.xlsx.feuilles || []).map((f) => (
                  <div key={f.nom} className="border border-zinc-200 rounded-lg" data-testid={`xlsx-apercu-feuille-${f.nom}`}>
                    <label className="flex items-center gap-3 px-4 py-2.5 border-b border-zinc-100 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={choisies.includes(f.nom)}
                        disabled={!f.exploitable}
                        onChange={() => basculerFeuille(it.id, f.nom)}
                        className="w-4 h-4 accent-[#002FA7]"
                        data-testid={`xlsx-apercu-case-${f.nom}`}
                      />
                      <span className="font-medium text-sm text-zinc-800">{f.nom}</span>
                      <span className="text-[11px] text-zinc-500">
                        {f.lignes} {L("ligne(s)", "row(s)")} · {f.colonnes} {L("colonne(s)", "column(s)")}
                      </span>
                      {!f.exploitable && (
                        <span className="text-[11px] text-amber-700 font-semibold" data-testid={`xlsx-apercu-vide-${f.nom}`}>
                          {L("feuille vide — rien à importer", "empty sheet — nothing to import")}
                        </span>
                      )}
                    </label>
                    {f.apercu?.length > 0 && (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <tbody>
                            {f.apercu.map((ligne, i) => (
                              <tr key={i} className={i === 0 ? "bg-zinc-50 font-semibold" : ""}>
                                {ligne.map((cellule, j) => (
                                  <td key={j} className="px-3 py-1.5 border-t border-zinc-100 text-zinc-700 whitespace-nowrap max-w-[220px] truncate">
                                    {cellule}
                                  </td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="px-5 py-4 border-t border-zinc-200 flex items-center justify-between gap-3">
                <button
                  type="button"
                  onClick={() => { removeItem(it.id); setApercuOuvert(null); }}
                  className="text-sm text-rose-600 hover:underline font-semibold"
                  data-testid="xlsx-apercu-retirer-fichier"
                >
                  {L("Retirer ce fichier", "Remove this file")}
                </button>
                <Button
                  onClick={() => setApercuOuvert(null)}
                  disabled={choisies.length === 0}
                  data-testid="xlsx-apercu-confirmer"
                >
                  {choisies.length === 0
                    ? L("Cochez au moins une feuille", "Check at least one sheet")
                    : L(`Confirmer l'import de ${choisies.length} feuille(s)`, `Confirm import of ${choisies.length} sheet(s)`)}
                </Button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
